import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SnackbarService } from 'src/app/main/backoffice/shared/services/snackbar.service';
import { minArrayLength } from 'src/app/main/backoffice/shared/utils/validators/validators.forms';
import { CrearPedidoDTO, Topping } from '../../models/crearPedido.dto';
import { PedidosService } from '../../services/pedidos.service';
import { ParametrosService } from '../../../parametros/services/parametros.service';
import { ProductosService } from '../../../productos/services/productos.service';
import { Categoria } from '../../../productos/models/categoria.model';
import { Producto } from '../../../productos/models/producto.model';

@Component({
  selector: 'app-crear-pedido',
  templateUrl: './crear-pedido.component.html',
  styleUrls: ['./crear-pedido.component.scss'],
})
export class CrearPedidoComponent implements OnInit {
  public form!: FormGroup;

  public tieneDelivery: boolean = false;

  categorias: Categoria[] = [];

  // productos disponibles POR item
  productosPorItem: Producto[][] = [];

  // Productos del menú (simples)
  public tiposHamburguesa = [
    { id: 1, nombre: 'Bora Clásica', precio: 3500 },
    { id: 2, nombre: 'Bora BBQ', precio: 3900 },
  ];

  public toppingsDisponibles: Topping[] = [];

  public get hamburguesas(): FormArray {
    return this.form.get('hamburguesas') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private pedidosService: PedidosService,
    private snackbar: SnackbarService,
    private router: Router,
    private parametrosService: ParametrosService,
    private productosService: ProductosService,
  ) {}

  ngOnInit(): void {
    this.obtenerCategorias();
    this.obtenerToppings();
    this.obtenerParametros();
    this.inicializarFormulario();
    this.suscribirseACambiosEnFormulario();
  }

  obtenerToppings(): void {
    this.productosService.obtenerToppings().subscribe((res) => {
      this.toppingsDisponibles = res;
    });
  }

  private obtenerCategorias(): void {
    this.productosService.obtenerCategorias().subscribe({
      next: (cats) => (this.categorias = cats),
    });
  }

  private inicializarFormulario(): void {
    this.form = this.fb.group({
      customerName: ['', Validators.required],
      customerPhone: ['', Validators.required],
      tipoPedido: ['retiro', Validators.required],
      deliveryAddress: ['', Validators.required],
      deliveryNotes: [null],
      tipoEntrega: ['ahora', Validators.required],
      hora: [''],
      paymentMethod: [1, Validators.required],
      notes: [''],
      deliveryFee: [0],
      hamburguesas: this.fb.array([], [minArrayLength(1)]),
      total: [{ value: 0, disabled: true }],
    });
  }

  private obtenerParametros(): void {
    this.parametrosService.obtenerParametros().subscribe({
      next: (params) => {
        const deliveryParam = params.find((p) => p.key === 'delivery_enabled');

        this.tieneDelivery = deliveryParam?.value === 'true';
      },
    });
  }

  private suscribirseACambiosEnFormulario(): void {
    //Si el formulario cambia recalculo totales
    this.form.valueChanges.subscribe(() => this.calcularTotal());

    //Si el tipo de pedido cambia oculto la dirección
    if (this.form.get('tipoPedido')?.value === 'retiro') {
      const direccionCtrl = this.form.get('deliveryAddress');
      direccionCtrl?.reset(null, { emitEvent: false });
      direccionCtrl?.clearValidators();
      direccionCtrl?.updateValueAndValidity();
    }

    this.form.get('tipoPedido')?.valueChanges.subscribe((tipo) => {
      const direccionCtrl = this.form.get('deliveryAddress');

      if (tipo === 'retiro') {
        direccionCtrl?.reset(null, { emitEvent: false });
        direccionCtrl?.clearValidators();
        direccionCtrl?.updateValueAndValidity();
      } else {
        direccionCtrl?.setValidators([Validators.required]);
        direccionCtrl?.updateValueAndValidity();
      }
    });
  }

  public agregarProducto(): void {
    this.hamburguesas.push(
      this.fb.group({
        categoria: [null, Validators.required],
        producto: [null, Validators.required],
        cantidad: [1, [Validators.required, Validators.min(1)]],
        toppings: [[]],
        notes: [''],
      }),
    );

    this.productosPorItem.push([]); // 👈 importante
  }

  public eliminarHamburguesa(i: number): void {
    this.hamburguesas.removeAt(i);
    this.productosPorItem.splice(i, 1);
    this.calcularTotal();
  }

  public onCategoriaChange(index: number): void {
    const grupo = this.hamburguesas.at(index) as FormGroup;
    const categoria = grupo.get('categoria')?.value;

    // limpio producto
    grupo.get('producto')?.reset();

    // limpio toppings SI no admite
    if (!categoria?.admiteToppings) {
      grupo.get('toppings')?.setValue([]);
    }

    if (!categoria) {
      this.productosPorItem[index] = [];
      return;
    }

    this.productosService
      .obtenerProductos({ categoryId: categoria.id, isAvailable: true })
      .subscribe((productos) => {
        this.productosPorItem[index] = productos;
      });
  }

  private calcularTotal(): void {
    let total = 0;

    this.hamburguesas.controls.forEach((h) => {
      const producto = h.get('producto')?.value;
      const cantidad = +h.get('cantidad')?.value || 0;
      const toppings = h.get('toppings')?.value || [];

      if (producto) {
        total += producto.price * cantidad;
      }

      toppings.forEach((t: any) => {
        total += t.price * cantidad;
      });
    });

    total += this.form.get('deliveryFee')?.value || 0;

    this.form.get('total')?.setValue(total, { emitEvent: false });
  }

  public guardarPedido(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const dto = this.armarPayload();

    this.pedidosService.crearPedido(dto).subscribe({
      next: () => {
        this.snackbar.success('Éxito', 'Pedido creado correctamente', 7000);
        this.form.reset();
        this.hamburguesas.clear();
        this.form.get('tipoEntrega')?.setValue('ahora');
      },
      error: (error: HttpErrorResponse) =>
        this.snackbar.error('Error', error.message, 7000),
    });
  }

  private expandirHamburguesas(hamburguesas: any[]): any[] {
    const items: any[] = [];

    hamburguesas.forEach((h) => {
      for (let i = 0; i < h.cantidad; i++) {
        items.push({
          productId: h.producto.id,
          notes: h.notes || null,
          toppings: h.toppings.map((t: any) => ({
            toppingId: t.id,
            quantity: 1,
          })),
        });
      }
    });

    return items;
  }

  private armarPayload(): CrearPedidoDTO {
    const f = this.form.value;

    return {
      customerName: f.customerName,
      customerPhone: f.customerPhone,

      deliveryType: f.tipoPedido === 'delivery' ? 2 : 1,
      deliveryAddress: f.tipoPedido === 'delivery' ? f.deliveryAddress : null,
      deliveryNotes: f.notes || null,

      paymentMethod: Number(f.paymentMethod),
      deliveryFee: Number(f.deliveryFee ?? 0),

      items: this.expandirHamburguesas(f.hamburguesas),
    };
  }

  public admiteToppings(index: number): boolean {
    const grupo = this.hamburguesas.at(index) as FormGroup;
    const categoria = grupo.get('categoria')?.value;

    if (!categoria) return false;

    return categoria.name.toLowerCase() !== 'bebidas';
  }

  public volver(): void {
    this.router.navigate(['admin/pedidos']);
  }
}
